import type { PServed } from '@wui/im'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import { PreferenceKey, usePreferencesStore } from '@/entities/preference'

import type { ChatFolder } from '../types/chat-folder'

type LegacyChatFolder = ChatFolder & {
  pServedUsers?: PServed[]
}

const normalizeFolder = (value: unknown): ChatFolder | null => {
  if (!value || typeof value !== 'object') {
    return null
  }

  const folder = value as LegacyChatFolder
  const pServeds = folder.pServeds ?? folder.pServedUsers

  if (
    typeof folder.id !== 'string'
    || typeof folder.name !== 'string'
    || !Array.isArray(pServeds)
    || !pServeds.every((pServed) => typeof pServed === 'string')
  ) {
    return null
  }

  return {
    id: folder.id,
    name: folder.name,
    pServeds,
  }
}

const normalizeFolders = (value: unknown): ChatFolder[] => {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .map(normalizeFolder)
    .filter((folder): folder is ChatFolder => folder !== null)
}

export const useChatFolders = () => {
  const preferencesStore = usePreferencesStore()
  const { preferences } = storeToRefs(preferencesStore)

  const folders = computed(() => normalizeFolders(preferences.value[PreferenceKey.CHAT_FOLDERS]))

  const saveFolders = async (nextFolders: ChatFolder[]) => {
    await preferencesStore.setPreferences(PreferenceKey.CHAT_FOLDERS, nextFolders)
  }

  const createFolder = async (name: string, pServeds: PServed[]) => {
    const folder: ChatFolder = {
      id: crypto.randomUUID(),
      name: name.trim(),
      pServeds: [...new Set(pServeds)],
    }

    await saveFolders([...folders.value, folder])
    return folder
  }

  const updateFolder = async (id: string, name: string, pServeds: PServed[]) => {
    const nextFolders = folders.value.map((folder) => (
      folder.id === id
        ? {
          ...folder,
          name: name.trim(),
          pServeds: [...new Set(pServeds)],
        }
        : folder
    ))

    await saveFolders(nextFolders)
  }

  const deleteFolder = async (id: string) => {
    await saveFolders(folders.value.filter((folder) => folder.id !== id))
  }

  const deleteFolders = async (ids: string[]) => {
    if (ids.length === 0) {
      return
    }

    const idsSet = new Set(ids)
    await saveFolders(folders.value.filter((folder) => !idsSet.has(folder.id)))
  }

  const reorderFolders = async (orderedIds: string[]) => {
    const foldersById = new Map(folders.value.map((folder) => [folder.id, folder]))
    const nextFolders = orderedIds
      .map((id) => foldersById.get(id))
      .filter((folder): folder is ChatFolder => folder !== undefined)

    if (nextFolders.length !== folders.value.length) {
      return
    }

    await saveFolders(nextFolders)
  }

  return {
    folders,
    createFolder,
    updateFolder,
    deleteFolder,
    deleteFolders,
    reorderFolders,
  }
}
